import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  DialogTitle,
} from '@mui/material';

import { WallInput, Workspace } from '../../mainApi';
import { SpaceDialogState } from '../../types';
import { PanelSection } from '../core/PanelSection';

type WhiteboardDialogProps = {
  dialogState: SpaceDialogState;
  isSaving: boolean;
  workspaces: Workspace[];
  initialTitle: string;
  initialWorkspaceTitle: string;
  initialPassword: string;
  onClose: () => void;
  onSubmit: (payload: WallInput) => void;
};

type WhiteboardFormState = {
  workspace: string;
  title: string;
  isPrivate: boolean;
  isPublic: boolean;
  canCollaborate: boolean;
  canRejoin: boolean;
  selfEdit: boolean;
  showCardOwnerNames: boolean;
  passwordProtect: boolean;
  password: string;
  includeWorkspaceLink: boolean;
  allowDrawingTools: boolean;
  allowCardCreation: boolean;
  allowCardUnlocking: boolean;
  allowCardLocking: boolean;
  allowClustering: boolean;
  allowCardResizing: boolean;
  allowAiTools: boolean;
};

const getDefaultState = (): WhiteboardFormState => ({
  workspace: '',
  title: '',
  isPrivate: false,
  isPublic: true,
  canCollaborate: true,
  canRejoin: true,
  selfEdit: true,
  showCardOwnerNames: true,
  passwordProtect: false,
  password: '',
  includeWorkspaceLink: false,
  allowDrawingTools: true,
  allowCardCreation: true,
  allowCardUnlocking: true,
  allowCardLocking: true,
  allowClustering: true,
  allowCardResizing: true,
  allowAiTools: true,
});

export const WhiteboardDialog = ({
  dialogState,
  isSaving,
  workspaces,
  initialTitle,
  initialWorkspaceTitle,
  initialPassword,
  onClose,
  onSubmit,
}: WhiteboardDialogProps) => {
  const [activeTab, setActiveTab] = useState(0);
  const [form, setForm] = useState<WhiteboardFormState>(getDefaultState());
  const [titleError, setTitleError] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const updateForm = <K extends keyof WhiteboardFormState>(
    field: K,
    value: WhiteboardFormState[K]
  ) => {
    setForm((state) => ({ ...state, [field]: value }));
  };

  useEffect(() => {
    if (!dialogState.open) {
      return;
    }

    const source = dialogState.space;
    setActiveTab(0);
    setTitleError('');
    setPasswordError('');
    setForm({
      workspace: initialWorkspaceTitle,
      title: initialTitle,
      isPrivate: (source?.isPrivate as boolean | undefined) ?? false,
      isPublic: (source?.isPublic as boolean | undefined) ?? true,
      canCollaborate: (source?.canCollaborate as boolean | undefined) ?? true,
      canRejoin: (source?.canRejoinSessions as boolean | undefined) ?? true,
      selfEdit: (source?.canOnlyEditOwn as boolean | undefined) ?? true,
      showCardOwnerNames: (source?.showCardOwnerNames as boolean | undefined) ?? true,
      passwordProtect: Boolean(initialPassword),
      password: initialPassword,
      includeWorkspaceLink: (source?.includeWorkspaceLink as boolean | undefined) ?? false,
      allowDrawingTools: (source?.allowDrawingTools as boolean | undefined) ?? true,
      allowCardCreation: (source?.allowCardCreation as boolean | undefined) ?? true,
      allowCardUnlocking: (source?.allowCardUnlocking as boolean | undefined) ?? true,
      allowCardLocking: (source?.allowCardLocking as boolean | undefined) ?? true,
      allowClustering: (source?.allowClustering as boolean | undefined) ?? true,
      allowCardResizing: (source?.allowCardResizing as boolean | undefined) ?? true,
      allowAiTools: (source?.allowAiTools as boolean | undefined) ?? true,
    });
  }, [dialogState.open, dialogState.space, initialTitle, initialWorkspaceTitle, initialPassword]);

  const selectedWorkspace = workspaces.find((workspace) => workspace.title === form.workspace);
  const workspaceLinkVisible = Boolean(selectedWorkspace?.externallyVisible && form.workspace);

  const validate = () => {
    let valid = true;
    setTitleError('');
    setPasswordError('');

    if (!form.title.trim()) {
      setTitleError('Required');
      valid = false;
    }

    if (form.passwordProtect && !form.password.trim()) {
      setPasswordError('Required');
      valid = false;
    }

    return valid;
  };

  const handleSubmit = () => {
    if (!validate()) {
      return;
    }

    onSubmit({
      workspace: form.workspace.trim(),
      title: form.title.trim(),
      password: form.passwordProtect ? form.password.trim() : null,
      isPrivate: form.isPrivate,
      isPublic: form.isPrivate ? true : form.isPublic,
      canCollaborate: form.isPrivate ? false : form.canCollaborate,
      canRejoin: form.isPrivate || !form.canCollaborate ? false : form.canRejoin,
      selfEdit: form.isPrivate || !form.canCollaborate ? false : form.selfEdit,
      showCardOwnerNames: form.showCardOwnerNames,
      includeWorkspaceLink: workspaceLinkVisible ? form.includeWorkspaceLink : false,
      allowDrawingTools: form.allowDrawingTools,
      allowCardCreation: form.allowCardCreation,
      allowCardUnlocking: form.allowCardUnlocking,
      allowCardLocking: form.allowCardLocking,
      allowClustering: form.allowClustering,
      allowCardResizing: form.allowCardResizing,
      allowAiTools: form.allowAiTools,
    });
  };

  return (
    <Dialog open={dialogState.open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ px: 1, pt: 1 }}>
        <Tabs value={activeTab} onChange={(_, value: number) => setActiveTab(value)}>
          <Tab
            label={
              dialogState.mode === 'create'
                ? 'New whiteboard'
                : dialogState.mode === 'edit'
                ? 'Edit whiteboard'
                : 'Copy whiteboard'
            }
          />
          <Tab label="Additional" />
        </Tabs>
      </DialogTitle>
      <DialogContent>
        <Stack mt={1} spacing={2.5}>
          {activeTab === 0 && (
            <>
              <PanelSection title="Details">
                <FormControl size="small" fullWidth>
                  <InputLabel id="whiteboard-workspace-label">Workspace (optional)</InputLabel>
                  <Select
                    labelId="whiteboard-workspace-label"
                    value={form.workspace}
                    label="Workspace (optional)"
                    onChange={(event) => updateForm('workspace', event.target.value)}
                  >
                    <MenuItem value="">None</MenuItem>
                    {workspaces.map((workspace) => (
                      <MenuItem key={workspace.id} value={workspace.title || ''}>
                        {workspace.title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {workspaceLinkVisible && (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.includeWorkspaceLink}
                        onChange={(event) =>
                          updateForm('includeWorkspaceLink', event.target.checked)
                        }
                      />
                    }
                    label="Workspace link visible in wall"
                  />
                )}
                <TextField
                  label="Wall title"
                  value={form.title}
                  onChange={(event) => updateForm('title', event.target.value)}
                  error={Boolean(titleError)}
                  helperText={titleError}
                  fullWidth
                />
              </PanelSection>

              <PanelSection title="Collaboration">
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.isPrivate}
                      onChange={(event) => updateForm('isPrivate', event.target.checked)}
                    />
                  }
                  label="This is a private wall (not sharable)"
                />
                {!form.isPrivate && (
                  <>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={form.canCollaborate}
                          onChange={(event) => updateForm('canCollaborate', event.target.checked)}
                        />
                      }
                      label="People viewing this whiteboard can make changes to it"
                    />

                    {form.canCollaborate && (
                      <FormGroup sx={{ pl: 2 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={form.canRejoin}
                              onChange={(event) => updateForm('canRejoin', event.target.checked)}
                            />
                          }
                          label="People can rejoin a board with the same name after leaving"
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={form.selfEdit}
                              onChange={(event) => updateForm('selfEdit', event.target.checked)}
                            />
                          }
                          label="People can only make changes to cards they have created"
                        />
                        {!form.selfEdit && (
                          <FormControlLabel
                            control={
                              <Switch
                                checked={form.showCardOwnerNames}
                                onChange={(event) =>
                                  updateForm('showCardOwnerNames', event.target.checked)
                                }
                              />
                            }
                            label="Show card owner names"
                          />
                        )}
                      </FormGroup>
                    )}
                  </>
                )}
              </PanelSection>
            </>
          )}

          {activeTab === 1 && (
            <>
              <PanelSection title="Security">
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.passwordProtect}
                      onChange={(event) => updateForm('passwordProtect', event.target.checked)}
                    />
                  }
                  label="Password protected"
                />
                {form.passwordProtect && (
                  <TextField
                    label="Password"
                    type="password"
                    value={form.password}
                    onChange={(event) => updateForm('password', event.target.value)}
                    error={Boolean(passwordError)}
                    helperText={passwordError}
                    fullWidth
                  />
                )}
              </PanelSection>
              <PanelSection title="Board Options">
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.allowDrawingTools}
                        onChange={(event) => updateForm('allowDrawingTools', event.target.checked)}
                      />
                    }
                    label="Drawing tools are enabled"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.allowCardCreation}
                        onChange={(event) => updateForm('allowCardCreation', event.target.checked)}
                      />
                    }
                    label="Allow cards to be created"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.allowCardUnlocking}
                        onChange={(event) => updateForm('allowCardUnlocking', event.target.checked)}
                      />
                    }
                    label="Allow cards and clusters to be unlocked"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.allowCardLocking}
                        onChange={(event) => updateForm('allowCardLocking', event.target.checked)}
                      />
                    }
                    label="Allow cards and clusters to be locked"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.allowAiTools}
                        onChange={(event) => updateForm('allowAiTools', event.target.checked)}
                      />
                    }
                    label="AI tools are enabled"
                  />
                </FormGroup>
              </PanelSection>

              <PanelSection title="Card Options">
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.allowClustering}
                        onChange={(event) => updateForm('allowClustering', event.target.checked)}
                      />
                    }
                    label="Allow cards to be clustered"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.allowCardResizing}
                        onChange={(event) => updateForm('allowCardResizing', event.target.checked)}
                      />
                    }
                    label="Allow cards and clusters to be resized"
                  />
                </FormGroup>
              </PanelSection>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={isSaving} variant="contained" color="primary">
          {dialogState.mode === 'edit' ? 'Update' : dialogState.mode === 'copy' ? 'Copy' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
